import { useEffect, useState } from 'react'
import Styles from './Chat.module.scss'
import cx from 'classnames'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { AI, Close } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { ChatAssistant } from 'components/ChatAssistant/ChatAssistant'
import { useIsScrolled } from 'hooks/useIsScrolled'
import { ChatHistory } from './components/ChatHistory'
import { collectResults } from 'utils/query'
import { useInfiniteThreads } from 'hooks/queries/chat/useInfiniteThreads'
import { ChatProps } from './Types'
import { Pill } from 'components/Pill/Pill'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const Chat = (props: ChatProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { fileId, handleClose, context, isOpen, showHistory, isNextGen, channel } = props
  const threadsQuery = useInfiniteThreads({ fileId })
  const threads = collectResults(threadsQuery, (data) => data.threads)
  const mostRecentThreadId = threads.length > 0 ? threads[0]?.id : undefined
  const [ref, setRef] = useState<HTMLDivElement | null>(null)
  const [selectedThreadId, setSelectedThreadId] = useState<number | undefined>(mostRecentThreadId)
  const isScrolled = useIsScrolled(ref)

  useEffect(() => {
    setSelectedThreadId(mostRecentThreadId)
  }, [mostRecentThreadId])

  if (!isOpen) return null

  return (
    <div className={Styles.root} id="ai-chat-root">
      {((!!showHistory && !!fileId) || !!handleClose || !isSystemRedesignEnabled) && (
        <div className={cx(Styles.header, { [Styles.scrolled]: isScrolled })}>
          {!isSystemRedesignEnabled && (
            <div className={Styles.title}>
              <AI size={24} />
              <Text variant="placeholder" color="var(--dark-80)" lineClamp={1}>
                Ask Blaze to Make Changes
              </Text>
              <Pill size="sm" className={Styles.betaPill}>
                BETA
              </Pill>
            </div>
          )}
          <div className={Styles.actions}>
            {showHistory && !!fileId && (
              <ChatHistory
                fileId={fileId}
                selectedThreadId={selectedThreadId}
                setSelectedThreadId={setSelectedThreadId}
              />
            )}
            {handleClose && (
              <IconButton
                rounded
                variant="editor"
                icon={() => <Close />}
                aria-label="Close chat"
                onPress={handleClose}
              />
            )}
          </div>
        </div>
      )}
      <div className={Styles.chatContainer} ref={setRef}>
        <ChatAssistant
          threadId={selectedThreadId}
          fileId={fileId}
          setSelectedThreadId={setSelectedThreadId}
          inputPlaceholder="Ask Blaze to change something..."
          context={context}
          isNextGen={isNextGen}
          channel={channel}
        />
      </div>
    </div>
  )
}

const ChatContainer = (props: ChatProps) => {
  const { isOpen } = props

  return (
    <div className={cx(Styles.chatAsideSpacer, { [Styles.open]: isOpen })}>
      <aside className={Styles.chatAside}>
        <Chat {...props} />
      </aside>
    </div>
  )
}

export { ChatContainer as Chat }
